/**
 * Token Sharing Utility
 * 
 * This utility handles secure token sharing between Clinical and Administration applications.
 * It provides methods to encode/decode tokens for URL parameters and manage token transfer.
 */

/**
 * Encodes a token for safe transmission via URL parameters
 * Uses base64 encoding with URL-safe characters
 * @param {string} token - The access token to encode
 * @returns {string} The encoded token
 */
export const encodeTokenForUrl = (token) => {
  if (!token) {
    return null;
  }
  
  try {
    // Use base64 encoding with URL-safe characters
    return btoa(token).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
  } catch (error) {
    return null;
  }
};

/**
 * Decodes a token from URL parameters
 * @param {string} encodedToken - The encoded token from URL
 * @returns {string|null} The decoded token or null if invalid
 */
export const decodeTokenFromUrl = (encodedToken) => {
  if (!encodedToken) {
    return null;
  }
  
  try {
    
    // Convert URL-safe base64 back to standard base64
    const standardBase64 = encodedToken.replace(/-/g, '+').replace(/_/g, '/');
    
    // Add padding if needed
    const padding = '='.repeat((4 - standardBase64.length % 4) % 4);
    const paddedBase64 = standardBase64 + padding;
    
    
    const decodedToken = atob(paddedBase64);
    
    
    return decodedToken;
  } catch (error) {
    return null;
  }
};

/**
 * Creates a URL with encoded token and userInfo parameters for switching to another application
 * @param {string} targetUrl - The target application URL
 * @param {string} token - The access token to pass
 * @param {string} sourceApp - The source application ('clinical' or 'admin')
 * @param {string} userInfo - The user info JSON string to pass (optional)
 * @returns {string} The complete URL with encoded token and userInfo
 */
export const createTokenizedUrl = (targetUrl, token, sourceApp, userInfo = null, additionalParams = null) => {
  if (!targetUrl || !token) {
    return targetUrl;
  }
  
  const encodedToken = encodeTokenForUrl(token);
  if (!encodedToken) {
    return targetUrl;
  }
  
  const url = new URL(targetUrl);
  
  // Add the encoded token as a URL parameter
  url.searchParams.set('token', encodedToken);
  
  // Add userInfo if provided
  if (userInfo) {
    const encodedUserInfo = encodeTokenForUrl(userInfo);
    if (encodedUserInfo) {
      url.searchParams.set('userInfo', encodedUserInfo);
    }
  }
  
  // Add source app identifier for tracking
  url.searchParams.set('from', sourceApp);
  
  // Add timestamp for token freshness validation
  url.searchParams.set('ts', Date.now().toString());
  
  if (additionalParams && typeof additionalParams === 'object') {
    Object.entries(additionalParams).forEach(([key, value]) => {
      if (value === undefined || value === null) {
        return;
      }
      
      let serializedValue = value;
      
      if (typeof serializedValue === 'object') {
        try {
          serializedValue = JSON.stringify(serializedValue);
        } catch (error) {
          return;
        }
      }
      
      const encodedValue = typeof serializedValue === 'string'
        ? encodeTokenForUrl(serializedValue) || serializedValue
        : serializedValue;
      
      url.searchParams.set(key, encodedValue);
    });
  }
  
  return url.toString();
};

/**
 * Extracts and validates token and userInfo from URL parameters
 * @param {string} url - The current URL (optional, defaults to window.location.href)
 * @returns {object} Object containing token, userInfo, source app, and validation status
 */
export const extractTokenFromUrl = (url = null) => {
  const currentUrl = url || window.location.href;
  const urlObj = new URL(currentUrl);
  
  const encodedToken = urlObj.searchParams.get('token');
  const encodedUserInfo = urlObj.searchParams.get('userInfo');
  const sourceApp = urlObj.searchParams.get('from');
  const timestamp = urlObj.searchParams.get('ts');
  const loginRequired = urlObj.searchParams.get('loginRequired');
  const moveDirect = urlObj.searchParams.get('moveDirect');
  if(moveDirect) {
    sessionStorage.setItem('moveDirect', moveDirect);
  }
  
  // Validate token freshness (5 minutes max)
  const tokenAge = timestamp ? Date.now() - parseInt(timestamp) : Infinity;
  const isTokenFresh = tokenAge < 5 * 60 * 1000; // 5 minutes in milliseconds
  
  const token = encodedToken ? decodeTokenFromUrl(encodedToken) : null;
  const userInfo = encodedUserInfo ? decodeTokenFromUrl(encodedUserInfo) : null;
  if(timestamp) {
    sessionStorage.setItem('lastLoginForAdmin', timestamp);
  }
  
  return {
    loginRequired,
    token,
    userInfo,
    sourceApp,
    isTokenFresh,
    hasToken: !!token,
    hasUserInfo: !!userInfo,
    tokenAge: tokenAge
  };
};

/**
 * Cleans up token and userInfo parameters from URL to prevent exposure
 * @param {string} url - The URL to clean (optional, defaults to current URL)
 * @returns {string} The cleaned URL without token and userInfo parameters
 */
export const cleanTokenFromUrl = (url = null) => {
  const currentUrl = url || window.location.href;
  const urlObj = new URL(currentUrl);
  
  // Remove token and userInfo related parameters
  urlObj.searchParams.delete('token');
  urlObj.searchParams.delete('userInfo');
  urlObj.searchParams.delete('from');
  urlObj.searchParams.delete('ts');
  
  return urlObj.toString();
};

/**
 * Stores token and userInfo in sessionStorage and cleans URL parameters
 * @param {string} token - The access token to store
 * @param {string|object} userInfo - User information to store (can be JSON string or object)
 * @param {boolean} cleanUrl - Whether to clean URL parameters (default: true)
 */
export const storeTokenFromUrl = (token, userInfo = null, cleanUrl = true) => {
  if (!token) {
    return false;
  }
  
  try {
    // Store token in sessionStorage
    sessionStorage.setItem('accessToken', token);
    
    // Store user info if provided
    if (userInfo) {
      // If userInfo is already a JSON string, store it directly
      // If it's an object, stringify it first
      const userInfoString = typeof userInfo === 'string' ? userInfo : JSON.stringify(userInfo);
      sessionStorage.setItem('userInfo', userInfoString);
    }
    
    // Clean URL parameters immediately to prevent token exposure
    if (cleanUrl) {
      const cleanUrl = cleanTokenFromUrl();
      window.history.replaceState({}, '', cleanUrl);
    }
    
    return true;
  } catch (error) {
    return false;
  }
};

/**
 * Checks if the current application should accept tokens from URL
 * This prevents token injection from untrusted sources
 * @param {string} sourceApp - The source application identifier
 * @returns {boolean} Whether the token should be accepted
 */
export const shouldAcceptToken = (sourceApp) => {
  // Define trusted source applications
  const trustedSources = ['clinical', 'admin'];
  
  return trustedSources.includes(sourceApp);
};

/**
 * Logs token transfer for debugging (without exposing the actual token)
 * @param {string} action - The action being performed
 * @param {string} sourceApp - The source application
 * @param {boolean} success - Whether the operation was successful
 */
export const logTokenTransfer = (action, sourceApp, success) => {
  if (process.env.NODE_ENV === 'development') {
  }
};

/**
 * Sanitizes console output to prevent token exposure
 * @param {string} message - The message to sanitize
 * @param {string} token - The token to mask
 * @returns {string} The sanitized message
 */
export const sanitizeLogMessage = (message, token = null) => {
  if (!token) {
    return message;
  }
  
  // Replace token with masked version for logging
  const maskedToken = token.substring(0, 8) + '...' + token.substring(token.length - 4);
  return message.replace(token, maskedToken);
};

/**
 * Validates token format before processing
 * @param {string} token - The token to validate
 * @returns {boolean} Whether the token format is valid
 */
export const isValidTokenFormat = (token) => {
  if (!token || typeof token !== 'string') {
    return false;
  }
  
  // Log token for debugging (first 20 chars only for security)
  const tokenPreview = token.length > 20 ? token.substring(0, 20) + '...' : token;
  
  // Simple validation: just check if token exists and has reasonable length
  // If it was successfully decoded from base64, it's probably valid
  const isValid = token.length >= 10;
  
  return isValid;
};

/**
 * Cleans up sensitive data from URL parameters after processing
 * This should be called after token processing to prevent token exposure in browser history
 */
export const cleanupSensitiveUrlParams = () => {
  try {
    const url = new URL(window.location.href);
    const sensitiveParams = ['token', 'userInfo', 'from', 'ts'];
    
    let hasSensitiveParams = false;
    sensitiveParams.forEach(param => {
      if (url.searchParams.has(param)) {
        url.searchParams.delete(param);
        hasSensitiveParams = true;
      }
    });
    
    if (hasSensitiveParams) {
      // Replace the current URL without sensitive parameters
      window.history.replaceState({}, '', url.toString());
    }
  } catch (error) {
  }
};
